import React, { useEffect, useState } from "react";
import { fetchAdminSuppliers, updateSupplierCommission } from "../../lib/adminApi";
import {
  Loader2,
  Save,
  CheckCircle2,
  KeyRound,
  Percent,
  ShoppingBag,
  Link2,
  AlertCircle,
  Info,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

export default function AdminSuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [dirty, setDirty] = useState({});

  const load = () =>
    fetchAdminSuppliers()
      .then((d) => setSuppliers(d.suppliers || []))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const updateField = (id, field, value) => {
    setDirty((d) => ({
      ...d,
      [id]: { ...(d[id] || {}), [field]: value },
    }));
  };

  const save = async (sup) => {
    const patch = dirty[sup.id] || {};
    if (Object.keys(patch).length === 0) return;
    setSavingId(sup.id);
    try {
      const parsed = {};
      if (patch.commission_rate !== undefined)
        parsed.commission_rate = parseFloat(patch.commission_rate) || 0;
      if (patch.panier_moyen !== undefined)
        parsed.panier_moyen = parseFloat(patch.panier_moyen) || 0;
      if (patch.affiliate_id !== undefined)
        parsed.affiliate_id = String(patch.affiliate_id).trim();
      if (patch.affiliate_url_template !== undefined)
        parsed.affiliate_url_template = String(patch.affiliate_url_template).trim();
      await updateSupplierCommission(sup.id, parsed);
      toast.success(`${sup.nom} mis à jour`);
      setDirty((d) => {
        const copy = { ...d };
        delete copy[sup.id];
        return copy;
      });
      await load();
    } catch (err) {
      toast.error("Impossible de sauvegarder");
    } finally {
      setSavingId(null);
    }
  };

  const value = (sup, field, fallback = "") =>
    dirty[sup.id]?.[field] !== undefined ? dirty[sup.id][field] : sup[field] ?? fallback;

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Chargement...
      </div>
    );

  return (
    <div data-testid="admin-suppliers-page" className="space-y-6">
      <header>
        <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
          Comptes affiliés & commissions
        </span>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-50">
          Fournisseurs
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          Pour chaque fournisseur : renseigne ton <strong>ID d'affiliation</strong> (dès que tu
          l'obtiens via Effinity, Kwanko, Awin…), le <strong>taux de commission</strong> et le
          <strong> panier moyen</strong>. Ces infos sont utilisées automatiquement dans tous
          les liens générés sur le site — tu n'as rien à toucher au code.
        </p>
        <div className="mt-3 inline-flex items-start gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-slate-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
          <div>
            Tu peux saisir soit un <strong>ID affilié court</strong> (ex. <code className="text-cyan-200">AP123456</code>),
            soit un <strong>lien d'affiliation complet</strong> fourni par la régie (ex.
            <code className="text-cyan-200"> https://trk.effiliation.com/servlet/effi.redir?id_compteur=XXXXX&amp;url=&#123;query&#125;</code>).
            Si tu colles un lien avec <code className="text-cyan-200">&#123;query&#125;</code>, il est injecté automatiquement.
          </div>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {suppliers.map((s) => (
          <SupplierCard
            key={s.id}
            supplier={s}
            dirty={dirty[s.id]}
            savingId={savingId}
            value={(f, fb) => value(s, f, fb)}
            onChange={(field, val) => updateField(s.id, field, val)}
            onSave={() => save(s)}
          />
        ))}
      </div>
    </div>
  );
}

const SupplierCard = ({ supplier: s, dirty, savingId, value, onChange, onSave }) => {
  const hasDirty = !!dirty && Object.keys(dirty).length > 0;
  const saving = savingId === s.id;
  const affConfigured = s.aff_id_configured;

  return (
    <div
      data-testid={`supplier-card-${s.id}`}
      className={`surface-card space-y-4 rounded-2xl p-5 ${
        affConfigured ? "border-emerald-500/20" : "border-amber-500/20"
      }`}
    >
      {/* Header fournisseur */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-heading text-lg font-black text-white shadow-md"
            style={{ background: s.couleur }}
          >
            {s.nom[0]}
          </div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-50">{s.nom}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <Badge
                variant="outline"
                className={`text-[9px] uppercase ${
                  s.type_pieces === "occasion"
                    ? "border-orange-500/40 bg-orange-500/10 text-orange-300"
                    : "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
                }`}
              >
                {s.type_pieces}
              </Badge>
              {s.supporte_auto && (
                <Badge
                  variant="outline"
                  data-testid={`supplier-badge-auto-${s.id}`}
                  className="border-cyan-500/40 bg-cyan-500/10 text-[9px] uppercase text-cyan-300"
                >
                  Auto
                </Badge>
              )}
              {s.supporte_moto && (
                <Badge
                  variant="outline"
                  data-testid={`supplier-badge-moto-${s.id}`}
                  className="border-amber-500/40 bg-amber-500/10 text-[9px] uppercase text-amber-300"
                >
                  Moto
                </Badge>
              )}
              {s.supporte_poids_lourd && (
                <Badge
                  variant="outline"
                  data-testid={`supplier-badge-pl-${s.id}`}
                  className="border-indigo-500/40 bg-indigo-500/10 text-[9px] uppercase text-indigo-300"
                >
                  PL
                </Badge>
              )}
            </div>
          </div>
        </div>

        {affConfigured ? (
          <Badge
            variant="outline"
            data-testid={`supplier-status-${s.id}`}
            className="border-emerald-500/40 bg-emerald-500/10 text-[9px] uppercase text-emerald-300"
          >
            <CheckCircle2 className="mr-1 h-3 w-3" /> Configuré
          </Badge>
        ) : (
          <Badge
            variant="outline"
            data-testid={`supplier-status-${s.id}`}
            className="border-amber-500/40 bg-amber-500/10 text-[9px] uppercase text-amber-300"
          >
            <AlertCircle className="mr-1 h-3 w-3" /> À configurer
          </Badge>
        )}
      </div>

      {/* ID affilié */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5 text-xs text-slate-300">
          <KeyRound className="h-3 w-3 text-cyan-400" />
          ID d'affiliation
          <code className="ml-1 rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[9px] text-slate-500">
            {s.env_key}
          </code>
        </Label>
        <Input
          data-testid={`supplier-affid-${s.id}`}
          type="text"
          value={value("affiliate_id", "")}
          onChange={(e) => onChange("affiliate_id", e.target.value)}
          placeholder="Ex. AP123456 ou code fourni par la régie"
          className="h-9 border-slate-700 bg-slate-900/60 text-slate-100 focus-visible:ring-cyan-500"
        />
        <p className="text-[10px] text-slate-500">
          Appliqué automatiquement sur tous les liens vers {s.nom}.
        </p>
      </div>

      {/* Lien complet (optionnel) */}
      <details className="group rounded-lg border border-slate-800/60 bg-slate-900/30">
        <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-cyan-300">
          <Link2 className="h-3 w-3" />
          Avancé — Lien d'affiliation complet (optionnel)
        </summary>
        <div className="space-y-1.5 border-t border-slate-800/60 p-3">
          <Input
            data-testid={`supplier-url-${s.id}`}
            type="text"
            value={value("affiliate_url_template", "")}
            onChange={(e) => onChange("affiliate_url_template", e.target.value)}
            placeholder="https://trk.effiliation.com/servlet/...&url={query}"
            className="h-9 border-slate-700 bg-slate-900/60 font-mono text-xs text-slate-100 focus-visible:ring-cyan-500"
          />
          <p className="text-[10px] text-slate-500">
            Utilise <code className="text-cyan-200">&#123;query&#125;</code> pour injecter la recherche et{" "}
            <code className="text-cyan-200">&#123;aff_id&#125;</code> pour ton ID. Laisse vide pour utiliser le lien par défaut.
          </p>
          <p className="text-[10px] text-slate-600">
            Modèle actuel :{" "}
            <code className="break-all text-slate-500">{s.url_template}</code>
          </p>
        </div>
      </details>

      {/* Taux + panier */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs text-slate-300">
            <Percent className="h-3 w-3 text-cyan-400" />
            Taux de commission
          </Label>
          <div className="relative">
            <Input
              data-testid={`supplier-rate-${s.id}`}
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={value("commission_rate", 0)}
              onChange={(e) => onChange("commission_rate", e.target.value)}
              className="h-9 border-slate-700 bg-slate-900/60 pr-8 text-slate-100 focus-visible:ring-cyan-500"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              %
            </span>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs text-slate-300">
            <ShoppingBag className="h-3 w-3 text-cyan-400" />
            Panier moyen
          </Label>
          <div className="relative">
            <Input
              data-testid={`supplier-basket-${s.id}`}
              type="number"
              step="1"
              min="0"
              value={value("panier_moyen", 0)}
              onChange={(e) => onChange("panier_moyen", e.target.value)}
              className="h-9 border-slate-700 bg-slate-900/60 pr-8 text-slate-100 focus-visible:ring-cyan-500"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              €
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-800/60 pt-3">
        <span className="text-[10px] text-slate-500">
          {s.updated_at
            ? `Dernière modif. : ${new Date(s.updated_at).toLocaleString("fr-FR")}`
            : "Jamais modifié"}
        </span>
        <Button
          data-testid={`supplier-save-${s.id}`}
          size="sm"
          disabled={!hasDirty || saving}
          onClick={onSave}
          className="h-8 gap-1.5 bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:opacity-40"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};
